/**
 * Affirmation — the Reading Room's post-round positive feedback card.
 *
 * This used to live in `juice/` as the "shared" affirmation; per the
 * foundation refactor it's now variant-owned. Each variant gets its own end-
 * of-round card with its own headline tone, glyph, motion vocab. The Reading
 * Room flavor is calm and bookish: a soft lamp-brighten beat, a category seal,
 * and a brief fan of paper score slips rather than a full-screen particle burst.
 *
 * Senior calibration baked in:
 *  - Warm headline, never empty hype
 *  - Specific praise about what they did, not just "good job"
 *  - Stats in plain interpretable units, never raw points alone
 *  - Two clear next-step CTAs, primary + secondary
 *  - Calm entrance — slide + fade, ~360ms, ease-out-quart
 *  - A restrained, reduced-motion-safe celebration on mount
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';

import { prefersReducedMotion, timings } from '@/juice';

import { Celebration } from './Celebration';

import './Affirmation.css';

export interface AffirmationProps {
  /** Small piece of context above the headline. e.g. "Sports · Practice round". */
  eyebrow?: string;
  /** Big, warm headline. e.g. "Nice work." */
  headline: string;
  /** Subhead — a specific, honest line about how they did. */
  subhead?: string;
  /** Bullet stats in plain units (not raw points). */
  stats?: ReadonlyArray<{ label: string; value: ReactNode }>;
  /** Body content (optional extras). */
  children?: ReactNode;
  /** Primary action (encouraged). */
  primaryAction?: { label: string; onClick: () => void };
  /** Secondary action (escape). */
  secondaryAction?: { label: string; onClick: () => void } | undefined;
  /**
   * When true, fires the Reading Room's lamp-and-paper celebration beat on
   * mount. Default true.
   */
  celebrate?: boolean;
  /**
   * Decorative glyph rendered above the headline, tinted with `--cat-accent`.
   * Default is a closed-book mark.
   */
  glyph?: ReactNode;
}

export function Affirmation({
  eyebrow,
  headline,
  subhead,
  stats,
  children,
  primaryAction,
  secondaryAction,
  celebrate = true,
  glyph,
}: AffirmationProps): React.JSX.Element {
  const [bursting, setBursting] = useState(false);
  const cardRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!celebrate) return;
    const delay = prefersReducedMotion() ? 0 : timings.slow + 120;
    const t = setTimeout(() => setBursting(true), delay);
    return () => clearTimeout(t);
  }, [celebrate]);

  return (
    <div className="affirmation" role="region" aria-live="polite">
      {/* A gentle lamp warm-up and small fan of paper score slips. */}
      <Celebration active={bursting} anchorRef={cardRef} onDone={() => setBursting(false)} />
      <div className="affirmation__card" ref={cardRef}>
        <div className="affirmation__hero">
          <div className="affirmation__glyph" aria-hidden="true">
            <span className="affirmation__glyph-ring" />
            <span className="affirmation__glyph-art">{glyph ?? <DefaultClosedBookGlyph />}</span>
          </div>
          <div className="affirmation__heading">
            {eyebrow ? <p className="affirmation__eyebrow">{eyebrow}</p> : null}
            <h2 className="affirmation__headline">{headline}</h2>
            {subhead ? <p className="affirmation__subhead">{subhead}</p> : null}
          </div>
        </div>
        {children ? <div className="affirmation__body">{children}</div> : null}
        {stats && stats.length > 0 ? (
          <dl className="affirmation__stats">
            {stats.map((s) => (
              <div className="affirmation__stat" key={s.label}>
                <dt>{s.label}</dt>
                <dd>{s.value}</dd>
              </div>
            ))}
          </dl>
        ) : null}
        {(primaryAction || secondaryAction) && (
          <div className="affirmation__actions">
            {secondaryAction ? (
              <button
                type="button"
                className="affirmation__btn affirmation__btn--secondary"
                onClick={secondaryAction.onClick}
              >
                {secondaryAction.label}
              </button>
            ) : null}
            {primaryAction ? (
              <button
                type="button"
                className="affirmation__btn affirmation__btn--primary"
                onClick={primaryAction.onClick}
                autoFocus
              >
                {primaryAction.label}
              </button>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

function DefaultClosedBookGlyph(): React.JSX.Element {
  return (
    <svg viewBox="0 0 56 56" fill="none" aria-hidden="true">
      <rect
        x="11"
        y="8"
        width="34"
        height="40"
        rx="3"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path d="M14 8v36" stroke="currentColor" strokeWidth="1.5" opacity="0.5" />
      <path
        d="M34 8v18l-3-3-3 3V8"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}
